import { useCallback, useEffect, useState } from "react";

import {
  ChevronDown,
  ChevronUp,
} from "@hl/base-components/lib/assets/icons/HDS/";
import {
  Check,
  Plus,
  PlusCircle,
} from "@hl/base-components/lib/assets/icons.generated";
import ModalSection from "@hl/base-components/lib/modal/ModalSection";
import {
  OUTLINE_COLOR,
  SECONDARY_COLOR,
} from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import { EXTERNAL_URL } from "@hl/shared-features/lib/config";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  Anchor,
  Box,
  Button,
  Collapse,
  Divider,
  Grid,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";

import { _CollectionType } from "apollo/graphql.generated";
import { GetCategoriesQuery } from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/queries.graphql.generated";
import useCategories from "~/features/collections/forms/CreateCollectionMultistepForm/tags-categories/useCategories";
type Props = {
  selectedCategories?: string[] | null;
  onChangeCategories: (categories: string[]) => void;
  collectionType: _CollectionType;
  isCurated: boolean;
};

type CategoryType = GetCategoriesQuery["getCategories"][0];

const CategoriesInput = ({
  selectedCategories,
  onChangeCategories,
  collectionType,
  isCurated,
}: Props) => {
  const { enableCategories, userCategories, categories, loading } =
    useCategories({
      selectedCategories,
      onChangeCategories,
      collectionType,
      isCurated,
    });
  const { pushModal, popModal } = useModalStack();

  if (!enableCategories) return <></>;

  if (loading) return <Loader />;

  const handleAddCategories = () =>
    pushModal(
      <AddCategories
        categories={categories}
        selectedCategories={selectedCategories}
        onChangeCategories={onChangeCategories}
        onClose={popModal}
      />,
      {
        size: "xl",
        title: (
          <Text size="md" fw={WEIGHT_BOLD}>
            Add categories
          </Text>
        ),
      }
    );

  return (
    <Group>
      <Text component="label">Categories</Text>
      <Text size="sm">
        Add categories that will appear on your collection page and help
        collectors discover your project based on the subject, format, style,
        and/or technology used.
      </Text>
      {userCategories?.map((category) => {
        return (
          <Button key={category?.slug} color={SECONDARY_COLOR}>
            {category?.name}
          </Button>
        );
      })}
      <Button
        leftIcon={<PlusCircle />}
        color={OUTLINE_COLOR}
        onClick={handleAddCategories}
      >
        Add categories
      </Button>
    </Group>
  );
};

const AddCategories = ({
  categories,
  selectedCategories,
  onChangeCategories,
  onClose,
}: {
  categories: CategoryType[];
  selectedCategories?: string[] | null;
  onChangeCategories: (categories: string[]) => void;
  onClose: () => void;
}) => {
  const [updatedCategories, setUpdatedCategories] = useState<string[]>(
    selectedCategories || []
  );
  const [search, setSearch] = useState<string>("");

  const isSelected = useCallback(
    (category: CategoryType) => {
      return updatedCategories?.includes(category.slug);
    },
    [updatedCategories]
  );

  const handleSave = useCallback(() => {
    onChangeCategories(updatedCategories);
    onClose();
  }, [updatedCategories, onClose]);

  const onRemoveCategory = useCallback(
    (slug: string) =>
      setUpdatedCategories(
        updatedCategories?.filter((category) => category !== slug) || []
      ),
    [updatedCategories]
  );

  const onAddCategory = useCallback(
    (slug: string) => {
      setUpdatedCategories([
        ...new Set([...(updatedCategories ? updatedCategories : []), slug]),
      ]);
    },
    [updatedCategories]
  );

  const disableAdd = updatedCategories.length >= 10;

  const filteredCategories = categories.filter((category) =>
    category.name.toLowerCase().includes(search.trim().toLowerCase())
  );

  const groupedCategories = filteredCategories.reduce(
    (groups: Map<string, CategoryType[]>, item) => {
      const { type } = item;
      const items = groups.get(type) || [];
      items.push(item);
      groups.set(type, items);
      return groups;
    },
    new Map()
  );

  const types = Array.from(groupedCategories.entries()).sort();

  return (
    <Stack spacing={0}>
      <TextInput
        mb="xl"
        placeholder="Search categories"
        onChange={(event) => setSearch(event.target.value)}
      />
      <Box mih={300}>
        {types.map(([type, categories], index) => (
          <CategoryType
            key={type}
            initialOpenedState={index === 0}
            hasSearched={!!search.trim()}
            categories={categories.sort((a, b) => a.name.localeCompare(b.name))}
            onAddCategory={onAddCategory}
            onChangeCategories={onChangeCategories}
            onRemoveCategory={onRemoveCategory}
            type={type}
            disableAdd={disableAdd}
            isSelected={isSelected}
          />
        ))}
      </Box>
      <Text size="xs" mt={16}>
        Are we missing something?{" "}
        <Anchor
          fw={WEIGHT_BOLD}
          size="xs"
          href={encodeURI(
            `mailto:${EXTERNAL_URL.HL_EMAIL}?subject=Category Suggestion`
          )}
          underline={false}
        >
          Please let us know.
        </Anchor>
      </Text>
      <ModalSection>
        <Button size="xl" onClick={handleSave} fullWidth>
          Save
        </Button>
      </ModalSection>
    </Stack>
  );
};

const CategoryType = ({
  initialOpenedState,
  hasSearched,
  type,
  categories,
  disableAdd,
  isSelected,
  onAddCategory,
  onRemoveCategory,
}: {
  initialOpenedState: boolean;
  hasSearched: boolean;
  type: string;
  categories: CategoryType[];
  onChangeCategories: (categories: string[]) => void;
  disableAdd: boolean;
  isSelected: (category: CategoryType) => boolean;
  onRemoveCategory: (slug: string) => void;
  onAddCategory: (slug: string) => void;
}) => {
  const [opened, { toggle, open, close }] = useDisclosure(initialOpenedState);
  const theme = useMantineTheme();

  useEffect(() => {
    if (initialOpenedState) return;
    if (hasSearched) open();
    else close();
  }, [hasSearched]);

  return (
    <Box>
      <Grid
        align="center"
        gutter="lg"
        onClick={toggle}
        style={{ cursor: "pointer" }}
      >
        <Grid.Col span="content">
          <Text tt="capitalize" color={TEXT_COLOR.PLACEHOLDER}>
            {type.toLowerCase()}
          </Text>
        </Grid.Col>
        <Grid.Col span="auto">
          <Divider />
        </Grid.Col>
        <Grid.Col span="content">
          {opened ? (
            <ChevronUp
              height={16}
              width={16}
              color={theme.fn.themeColor(TEXT_COLOR.PLACEHOLDER)}
            />
          ) : (
            <ChevronDown
              height={16}
              width={16}
              color={theme.fn.themeColor(TEXT_COLOR.PLACEHOLDER)}
            />
          )}
        </Grid.Col>
      </Grid>
      <Collapse in={opened}>
        <SimpleGrid cols={4} my="sm" p={8}>
          {categories.map((category) => {
            const selected = isSelected(category);
            return (
              <Button
                disabled={disableAdd && !selected}
                key={category.slug}
                onClick={() =>
                  selected
                    ? onRemoveCategory(category.slug)
                    : onAddCategory(category.slug)
                }
                color={selected ? SECONDARY_COLOR : OUTLINE_COLOR}
                rightIcon={selected ? <Check /> : <Plus />}
              >
                {category.name}
              </Button>
            );
          })}
        </SimpleGrid>
      </Collapse>
    </Box>
  );
};

export default CategoriesInput;
